import React, { useCallback, useRef } from 'react';
import { MultiStepForm, Step } from '../../../components/molecules/Form/components/MultiStepForm';
import { Checkbox, CheckBoxParam, CheckRadioForLists, Form, SonForm, SubmitButton, useForm } from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { PageType } from '../../../types/api/PageType';
import { useDelayed } from '../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useModalContext } from '../../../components/organisms/ModalDialog/ModalContext';
import { CheckBoxList } from '../partials/CheckBoxList';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { BlockType } from '../../../types/api/ElementType';
import { translatePageType } from '../../../_lib/components/PageType/PageTypeTitle';
import { PageTypeIcon } from '../../../_lib/components/PageType';

enum PrivilegeMultiStepFormId {
  STEP_PRIVILEGE = 'step-1-privilege',
  STEP_PAGE_TYPE = 'step-2-page-types',
}

type CreateEditPrivilege = {
  form: SonForm<CheckBoxParam<'privileges'> | CheckBoxParam<'forPageTypes'>>;
  initialActiveTab?: PrivilegeMultiStepFormId;
};

export function BasicPrivilegeConfigEditForm({
  form,
  initialActiveTab = PrivilegeMultiStepFormId.STEP_PRIVILEGE,
}: CreateEditPrivilege) {
  const { t, i18n } = useSonTranslation('PrivilegeConfig');
  const step2CheckboxesContainer = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const { refresh } = useRefreshBlock();
  const { closeModal } = useModalContext();
  const containerId = useHtmlValidId();

  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the additional privileges block.
    // see the asFetchableBlock call in AdditionalPrivilegeConfigBlock.tsx
    invalidateRefreshableBlock(BlockType.additionalprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  const onSuccess = () => {
    delayedRefreshAndClose();
  };

  const { formProps, params, fetchState } = useForm({ form, onSuccess });

  const getDescription = (param: CheckBoxParam<'privileges'> | CheckBoxParam<'forPageTypes'>) => {
    if (param.descriptionKey) {
      return t(`input.${param.descriptionKey}.description` as any);
    }

    if (i18n.exists(`PrivilegeConfig:input.${param.value}.description` as any)) {
      return t(`input.${param.value}.description` as any);
    }

    return undefined;
  };

  return (
    <Form {...formProps} modifier="no-padding">
      <MultiStepForm initialActiveTab={initialActiveTab}>
        <Step id={PrivilegeMultiStepFormId.STEP_PRIVILEGE} buttonLabel={t('glossary:privilege')}>
          <div className="privilege-config__multistep-content-container">
            <CheckBoxList>
              {params.getAllByName('privileges')?.map(
                (param, i) => (
                  <li key={param.name + i}>
                    <CheckRadioForLists
                      type="checkbox"
                      param={param}
                      // @ts-expect-error expression to complicated for type to resolve correct key and check existence
                      label={t(`input.${param.value?.toLocaleLowerCase()}.label`)}
                      // @ts-expect-error expression to complicated for type to resolve correct key and check existence
                      description={t(`input.${param.value?.toLocaleLowerCase()}.description`)}
                    />
                  </li>
                ))}
            </CheckBoxList>
          </div>
        </Step>
        <Step
          id={PrivilegeMultiStepFormId.STEP_PAGE_TYPE}
          buttonLabel={t('glossary:pageType_plural')}
          submitButton={(
            <SubmitButton
              buttonLabel={t('addPrivileges')}
              messageSuccess={t('message.success')}
              messageSending={t('message.sending')}
              messageFail={t('message.failure')}
              fetchState={fetchState}
              customErrorTypeMessages={{
                missingButRequired: t('message.missingBasicPrivilege'),
              }}
            />
          )}
        >
          <div className="privilege-config__multistep-content-container">
            {/* This check box will be ignored on submit as it has no name and is only used to select deselect all
            other page related check boxes. */}
            <Checkbox
              aria-controls={containerId}
              ref={selectAllRef}
              label={t('selectAll')}
              onChange={(event) => {
                const checkboxes = step2CheckboxesContainer.current?.querySelectorAll('[type="checkbox"]');

                checkboxes?.forEach((checkbox) => {
                  (checkbox as HTMLInputElement).checked = event.target.checked;
                });
              }}
            />
            <CheckBoxList ref={step2CheckboxesContainer} id={containerId}>
              {params.getAllByName('forPageTypes')?.map(
                (param: CheckBoxParam<'forPageTypes'>, i: number) => (
                  <li key={param.name + i}>
                    <CheckRadioForLists
                      type="checkbox"
                      param={param}
                      label={translatePageType(param.value as PageType)}
                      description={getDescription(param)}
                      icon={<PageTypeIcon pageType={param?.value as PageType} />}
                      onChange={() => {
                        if (selectAllRef.current?.checked) {
                          selectAllRef.current.checked = false;
                        }
                      }}
                    />
                  </li>
                )).sort((a: any, b: any) => {
                return a.props.children.props.label.localeCompare(b.props.children.props.label);
              })}
            </CheckBoxList>
          </div>
        </Step>
      </MultiStepForm>
    </Form>
  );
}
